import { useQuery } from 'react-query';
import { settingsManager } from '../../shared/settings';
import { useAuth } from '@iamexperiences/feature-auth';
import { RoleAllowedLists } from './RoleBasedAccessControl/RoleAllowedlists';
import { ActiveDirectoryRole } from './RoleBasedAccessControl/ActiveDirectoryRole';

const administrativeUnitScopedPathConstant = '/administrativeUnits/';
const rootScopedPath = '/';

interface IRoleAssignment {
  id: string;
  principalId: string;
  resourceScope: string;
  roleDefinitionId: ActiveDirectoryRole; // id of the role
}

const msGraphBaseUrl = settingsManager.getRequired('msGraphResourceName');

const useGetAdministrativeUnits = () => {
  const auth = useAuth();
  return useQuery({
    queryKey: 'administrativeUnits',
    queryFn: async () => {
      const token = await auth.auth.acquireToken({ scopes: [msGraphBaseUrl] });
      const url = `${msGraphBaseUrl}/beta/users/${auth.auth.user?.objectId}/roleManagement/directory/transitiveRoleAssignments?$count=true`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, ConsistencyLevel: 'eventual' },
      });
      if (!response.ok) throw new Error(`${response.status}: Failed to fetch role assignments.`);
      const roles: IRoleAssignment[] = (await response.json()).value;
      return roles;
    },
    staleTime: Infinity,
  });
};

/**
 * Returns true if role is not scope to AU (meaning scope to directory instead) && the user has
 * the CompanyAdministrator role ( alternative name is Global Administrator )
 *
 *  roleAssignments: a list of metadata for each AU
 */
const getIsGlobalAdministrator = (roleAssignments: readonly IRoleAssignment[]): boolean => {
  return roleAssignments.some((roleAssignment) => {
    const scope = roleAssignment.resourceScope;
    return scope === rootScopedPath && roleAssignment.roleDefinitionId === ActiveDirectoryRole.CompanyAdministrator;
  });
};

/**
 * Returns true if this user is allowed to view based on the role
 */
const getAllowedAdminUnits = (roleAssignments: ReadonlyArray<IRoleAssignment>) => {
  const isGlobalAdmin = getIsGlobalAdministrator(roleAssignments);
  const isWhiteListedForAllAdminUnits = roleAssignments.some((roleAssignment) => {
    const scope = roleAssignment.resourceScope;
    return scope === rootScopedPath && RoleAllowedLists.CanViewAdministrativeUnits.has(roleAssignment.roleDefinitionId);
  });
  if (isWhiteListedForAllAdminUnits || isGlobalAdmin) {
    return true;
  }
  return roleAssignments.some((roleAssignment) => {
    const scope = roleAssignment.resourceScope;
    const roleIsAdminUnitScoped = scope.startsWith(administrativeUnitScopedPathConstant);
    const roleIsWhitelisted = RoleAllowedLists.CanViewAdministrativeUnits.has(roleAssignment.roleDefinitionId);
    return roleIsAdminUnitScoped && roleIsWhitelisted;
  });
};

export const useCanViewAdminUnits = () => {
  const adminUnits = useGetAdministrativeUnits().data;
  return adminUnits ? getAllowedAdminUnits(adminUnits) : false;
};
